import React, { useMemo, useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  ColumnDef,
  SortingState,
  PaginationState,
  OnChangeFn,
  RowSelectionState,
} from '@tanstack/react-table';
import './TanStackGrid.css';
import {
  DropdownOption,
  ColumnFilterProps,
  OperatorOption,
  MenuCoords,
  CustomColumnDef,
  TanStackGridProps
} from './types';

// Column Filter Component
function ColumnFilter({
  columnId,
  filterType,
  value,
  operator,
  onValueChange,
  onOperatorChange,
  dropdownOptions,
}: ColumnFilterProps) {
  const [showOperatorMenu, setShowOperatorMenu] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Enable filter operator options for text fields that support contains/equals
  const supportsContains = [
    'geneSymbol',
    'gene_symbol', 
    'chem_name',
    'diseaseName',
    'geneDescription',
    'disease1_Name',
    'disease2_Name', 
    'name',
    'chemgda',
    'chemvda',
    'ChemPrefName',
    'chemPrefName',
    'symbol',
    'description'
  ].includes(columnId);

  const operatorOptions: OperatorOption[] =
    filterType === 'number'
      ? [
          { value: 'eq', label: 'Equals' },
          { value: 'gt', label: 'Greater than' },
          { value: 'lt', label: 'Less than' },
          { value: 'gte', label: 'Greater or equal' },
          { value: 'lte', label: 'Less or equal' },
        ]
      : supportsContains
        ? [
            { value: 'eq', label: 'Equals' },
            { value: 'contains', label: 'Contains' },
          ]
        : [{ value: 'eq', label: 'Equals' }];

  const defaultOperator = filterType === 'number' ? 'eq' : supportsContains ? 'eq' : 'eq';
  const currentOperator = operator || defaultOperator;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowOperatorMenu(false);
      }
    }

    function updatePosition() {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const spaceOnLeft = rect.left;
        const position = spaceOnLeft < 180 ? 'right' : 'left';

        setMenuCoords({
          top: rect.bottom + 4,
          left: position === 'left' ? rect.right - 160 : rect.left,
        });
      }
    }

    if (showOperatorMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [showOperatorMenu]);

  const [menuCoords, setMenuCoords] = useState<MenuCoords>({ top: 0, left: 0 });

  useEffect(() => {
    if (showOperatorMenu && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceOnLeft = rect.left;

      const position = spaceOnLeft < 180 ? 'right' : 'left';

      setMenuCoords({
        top: rect.bottom + 4,
        left: position === 'left' ? rect.right - 160 : rect.left,
      });
    }
  }, [showOperatorMenu]);

  const renderMenu = () => {
    if (!showOperatorMenu) return null;

    return ReactDOM.createPortal(
      <div
        ref={menuRef}
        className="filter-operator-menu-portal"
        style={{
          position: 'fixed',
          top: `${menuCoords.top}px`,
          left: `${menuCoords.left}px`,
          zIndex: 99999,
          background: 'white',
          border: '1px solid #ddd',
          borderRadius: '4px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          minWidth: '160px',
          overflow: 'hidden',
        }}
      >
        {operatorOptions.map(opt => (
          <div
            key={opt.value}
            className={`filter-operator-option ${currentOperator === opt.value ? 'active' : ''}`}
            onClick={() => {
              onOperatorChange(opt.value);
              setShowOperatorMenu(false);
            }}
          >
            {opt.label}
          </div>
        ))}
      </div>,
      document.body,
    );
  };

  return (
    <div className="column-filter" onClick={e => e.stopPropagation()}>
      <div className="filter-input-wrapper">
        {filterType === 'select' && dropdownOptions ? (
          <select
            value={value}
            onChange={e => onValueChange(e.target.value)}
            className="filter-input filter-select"
          >
            <option value="">All</option>
            {dropdownOptions.map(option => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={filterType === 'number' ? 'number' : 'text'}
            value={value}
            onChange={e => onValueChange(e.target.value)}
            placeholder="Filter..."
            className="filter-input"
          />
        )}
        {value && (
          <button
            className="filter-clear-button"
            onClick={e => {
              e.stopPropagation();
              onValueChange('');
            }}
            title="Clear filter"
          >
            ×
          </button>
        )}
        {filterType !== 'select' && operatorOptions.length > 1 && (
          <div className="filter-menu-wrapper">
            <button
              ref={buttonRef}
              className="filter-menu-button"
              onClick={e => {
                e.stopPropagation();
                setShowOperatorMenu(!showOperatorMenu);
              }}
              title="Filter operator"
            >
              ⋮
            </button>
          </div>
        )}
      </div>
      {renderMenu()}
    </div>
  );
}

function TanStackGrid({
  columns,
  data,
  pagination = true,
  pageSize = 10,
  pageSizeOptions = [10, 20, 50, 100],
  onRowSelectionChange,
  enableRowSelection = false,
  manualPagination = false,
  totalCount = 0,
  pageIndex = 0,
  onPageChange,
  onPageSizeChange,
  manualSorting = false,
  onSortingChange,
  sorting = [],
  loading = false,
  showFilters = false,
  filterValues = {},
  filterOperators = {},
  onFilterChange,
  onFilterOperatorChange,
  columnOrder: externalColumnOrder,
  onColumnOrderChange,
  columnVisibilityModel: externalColumnVisibilityModel,
  storageKey,
}: TanStackGridProps) {
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => {
    if (externalColumnVisibilityModel && Object.keys(externalColumnVisibilityModel).length > 0) {
      return externalColumnVisibilityModel;
    }
    return {};
  });

  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    if (externalColumnOrder && externalColumnOrder.length > 0) {
      return externalColumnOrder;
    }

    const defaultOrder = columns?.map(col => col.name) || [];
    return defaultOrder;
  });

  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [columnSizing, setColumnSizing] = useState<Record<string, number>>({});

  useEffect(() => {
    if (externalColumnVisibilityModel && Object.keys(externalColumnVisibilityModel).length > 0) {
      if (JSON.stringify(externalColumnVisibilityModel) !== JSON.stringify(columnVisibility)) {
        setColumnVisibility(externalColumnVisibilityModel);
        if (storageKey) {
          localStorage.removeItem(`columnVisibility_${storageKey}`);
        }
      }
    }
  }, [externalColumnVisibilityModel, columns, externalColumnOrder, storageKey]);

  const prevExternalOrderRef = useRef(externalColumnOrder);

  useEffect(() => {
    if (JSON.stringify(prevExternalOrderRef.current) === JSON.stringify(externalColumnOrder)) {
      return;
    }

    prevExternalOrderRef.current = externalColumnOrder;

    if (externalColumnOrder && externalColumnOrder.length > 0) {
      if (JSON.stringify(externalColumnOrder) !== JSON.stringify(columnOrder)) {
        setColumnOrder(externalColumnOrder);
      }
    }
  }, [externalColumnOrder, columns, columnOrder]);

  const formatNumber = (num: number): string => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  const prevColumnsRef = useRef<string[] | undefined>(undefined);
  const isUpdatingOrderRef = useRef<boolean>(false);

  useEffect(() => {
    if (isUpdatingOrderRef.current || draggedColumn) return;

    if (columns && columns.length > 0) {
      const currentColumnIds = columns.map(col => col.name);
      const prevColumnIds = prevColumnsRef.current;

      if (
        !prevColumnIds ||
        JSON.stringify(prevColumnIds.sort()) !== JSON.stringify(currentColumnIds.sort())
      ) {
        if (!externalColumnOrder || externalColumnOrder.length === 0) {
          const hasAllColumns = currentColumnIds.every(id => columnOrder.includes(id));
          const hasSameLength = currentColumnIds.length === columnOrder.length;

          if (!hasAllColumns || !hasSameLength) {
            isUpdatingOrderRef.current = true;
            let newOrder = [
              ...columnOrder.filter(id => currentColumnIds.includes(id)),
              ...currentColumnIds.filter(id => !columnOrder.includes(id)),
            ];
            setColumnOrder(newOrder);
            setTimeout(() => {
              isUpdatingOrderRef.current = false;
            }, 100);
          }
        }
        prevColumnsRef.current = currentColumnIds;
      }
    }
  }, [columns, columnOrder, draggedColumn, externalColumnOrder]);

  const prevColumnOrderRef = useRef(columnOrder);

  useEffect(() => {
    if (JSON.stringify(prevColumnOrderRef.current) !== JSON.stringify(columnOrder)) {
      prevColumnOrderRef.current = columnOrder;

      if (onColumnOrderChange && !isUpdatingOrderRef.current) {
        onColumnOrderChange(columnOrder);
      }
    }
  }, [columnOrder, onColumnOrderChange, externalColumnOrder]);

  const handleDragStart = (e: React.DragEvent<HTMLSpanElement>, columnId: string) => {
    setDraggedColumn(columnId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget.innerHTML);
  };

  const handleDragOver = (e: React.DragEvent<HTMLTableCellElement>, columnId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedColumn && draggedColumn !== columnId) {
      setDragOverColumn(columnId);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLTableCellElement>, targetColumnId: string) => {
    e.preventDefault();
    if (!draggedColumn || draggedColumn === targetColumnId) return;

    const newOrder = [...columnOrder];
    const draggedIndex = newOrder.indexOf(draggedColumn);
    const targetIndex = newOrder.indexOf(targetColumnId);

    if (draggedIndex !== -1 && targetIndex !== -1) {
      newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, draggedColumn);
      setColumnOrder(newOrder);
    }
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  const tableColumns = useMemo<ColumnDef<any>[]>(() => {
    if (!columns) return [];

    return columns.map(col => ({
      accessorKey: col.name,
      id: col.name,
      header: () => {
        if (React.isValidElement(col.header)) {
          return col.header;
        }
        if (typeof col.header === 'string') {
          return col.header;
        }
        return col.name;
      },
      cell: col.render
        ? (info: any) => {
            try {
              if (!info.row.original) return null;
              const result = col.render({ data: info.row.original, value: info.getValue() });
              return result;
            } catch (error) {
              // Silent error handling - return empty string for failed renders
              return '';
            }
          }
        : (info: any) => {
            const value = info.getValue();
            return value != null ? value : '';
          },
      enableSorting: col.sortable !== false,
      enableColumnFilter: true,
      size: col.minWidth || 150,
      minSize: col.minWidth || 100,
      maxSize: 800,
      meta: {
        filterType: col.filterType || (col.type === 'number' ? 'number' : 'text'),
        dropdownOptions: col.dropdownOptions,
      },
    }));
  }, [columns]);

  const validColumnOrder = useMemo(() => {
    if (!tableColumns || tableColumns.length === 0) return [];
    if (!columnOrder || columnOrder.length === 0) {
      return tableColumns.map(col => col.id as string);
    }

    const currentColumnIds = tableColumns.map(col => col.id as string);
    const isValid =
      columnOrder.length === currentColumnIds.length &&
      columnOrder.every(id => currentColumnIds.includes(id));

    if (isValid) {
      return columnOrder;
    } else {
      const validOrder = columnOrder.filter(id => currentColumnIds.includes(id));
      const missingColumns = currentColumnIds.filter(id => !columnOrder.includes(id));
      return [...validOrder, ...missingColumns];
    }
  }, [columnOrder, tableColumns]);

  const table = useReactTable({
    data: data || [],
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: manualPagination ? undefined : pagination ? getPaginationRowModel() : undefined,
    manualPagination: manualPagination,
    manualSorting: manualSorting,
    pageCount: manualPagination ? Math.ceil(totalCount / pageSize) : undefined,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    state: {
      pagination: manualPagination
        ? {
            pageIndex: pageIndex,
            pageSize: pageSize,
          }
        : undefined,
      sorting: manualSorting ? sorting : undefined,
      columnOrder: validColumnOrder,
      columnSizing: columnSizing,
      columnVisibility: columnVisibility,
    },
    onColumnSizingChange: setColumnSizing,
    onPaginationChange: manualPagination
      ? (updater: any) => {
          const newState: PaginationState =
            typeof updater === 'function' ? updater({ pageIndex, pageSize }) : updater;
          if (newState.pageIndex !== pageIndex && onPageChange) {
            onPageChange(newState.pageIndex);
          }
          if (newState.pageSize !== pageSize && onPageSizeChange) {
            onPageSizeChange(newState.pageSize);
          }
        }
      : undefined,
    onSortingChange: manualSorting ? onSortingChange : undefined,
    onColumnOrderChange: (newOrder: string[]) => {
      setColumnOrder(newOrder);
    },
    onColumnVisibilityChange: setColumnVisibility,
    enableRowSelection: enableRowSelection,
    onRowSelectionChange: onRowSelectionChange,
    autoResetAll: false,
  });

  return (
    <div className="tanstack-grid-container">
      {!loading && (!data || data.length === 0) && (
        <div className="tanstack-empty-state">
          <div className="empty-state-icon">
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M9 2v6h6V2M19 2v6M5 2v6M3 10h18M5 22h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              <path d="M7 14h.01M12 14h.01M17 14h.01M7 18h.01M12 18h.01M17 18h.01" />
            </svg>
          </div>
          <h3 className="empty-state-title">No results found</h3>
          <p className="empty-state-message">Try adjusting your search or filter criteria.</p>
        </div>
      )}
      {loading && (
        <>
          <div className="tanstack-loading-state">
            <div className="loader-spinner"></div>
            <p>Loading data...</p>
          </div>
        </>
      )}
      <div className="tanstack-grid-wrapper">
        <table className="tanstack-grid" style={{ opacity: loading ? 0.5 : 1 }}>
          <thead>
            {table.getHeaderGroups().map(headerGroup => {
              const orderedHeaders = validColumnOrder.length > 0 
                ? validColumnOrder.map(columnId => 
                    headerGroup.headers.find(header => header.column.id === columnId)
                  ).filter(Boolean)
                : headerGroup.headers;

              return (
                <tr key={headerGroup.id}>
                  {orderedHeaders.map(header => (
                    <th
                      key={header.id}
                      style={{
                        minWidth: header.column.columnDef.minSize,
                        width: header.getSize(),
                        position: 'relative',
                      }}
                      onDragOver={e => handleDragOver(e, header.column.id)}
                      onDragLeave={() => setDragOverColumn(null)}
                      onDrop={e => handleDrop(e, header.column.id)}
                      className={`
                        ${draggedColumn === header.column.id ? 'dragging-column' : ''}
                        ${dragOverColumn === header.column.id ? 'drag-over-column' : ''}
                      `.trim()}
                    >
                    {header.isPlaceholder ? null : (
                      <>
                        <div
                          className={`grid-header-content ${header.column.getCanSort() ? 'sortable-header' : ''}`}
                          draggable
                          onDragStart={e => handleDragStart(e, header.column.id)}
                          onDragEnd={handleDragEnd}
                          onClick={e => {
                            if (!draggedColumn && header.column.getCanSort()) {
                              const sortHandler = header.column.getToggleSortingHandler();
                              if (sortHandler) sortHandler(e);
                            }
                          }}
                        >
                          <span className="drag-handle" title="Drag to reorder">
                            ⋮⋮
                          </span>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getIsSorted() && (
                            <span className="sort-indicator">
                              {header.column.getIsSorted() === 'asc' ? ' ↑' : ' ↓'}
                            </span>
                          )}
                        </div>
                        {showFilters && header.column.getCanFilter() && (
                          <ColumnFilter
                            columnId={header.column.id}
                            filterType={(header.column.columnDef.meta as any)?.filterType || 'text'}
                            value={filterValues[header.column.id] || ''}
                            operator={filterOperators[header.column.id]}
                            onValueChange={value => onFilterChange && onFilterChange(header.column.id, value)}
                            onOperatorChange={op =>
                              onFilterOperatorChange && onFilterOperatorChange(header.column.id, op)
                            }
                            dropdownOptions={(header.column.columnDef.meta as any)?.dropdownOptions}
                          />
                        )}
                        <div
                          onMouseDown={e => {
                            e.stopPropagation();
                            header.getResizeHandler()(e);
                          }}
                          onTouchStart={e => {
                            e.stopPropagation();
                            header.getResizeHandler()(e);
                          }}
                          onDragStart={e => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          className={`resizer ${header.column.getIsResizing() ? 'isResizing' : ''}`}
                        />
                      </>
                    )}
                  </th>
                  ))}
                </tr>
              );
            })}
          </thead>
          <tbody>
            {data &&
              data.length > 0 &&
              table.getRowModel().rows.map(row => {
                const orderedCells = validColumnOrder.length > 0
                  ? validColumnOrder.map(columnId =>
                      row.getVisibleCells().find(cell => cell.column.id === columnId)
                    ).filter(Boolean)
                  : row.getVisibleCells();

                return (
                  <tr key={row.id}>
                    {orderedCells.map(cell => (
                      <td key={cell.id} style={{ width: cell.column.getSize() }}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {pagination && (
        <div className="tanstack-pagination">
          <div className="pagination-info">
            {manualPagination ? (
              <>
                Showing {formatNumber(pageIndex * pageSize + 1)} to{' '}
                {formatNumber(Math.min((pageIndex + 1) * pageSize, totalCount))} of {formatNumber(totalCount)}{' '}
                results
              </>
            ) : (
              <>
                Showing{' '}
                {formatNumber(
                  table.getState().pagination!.pageIndex * table.getState().pagination!.pageSize + 1,
                )}{' '}
                to{' '}
                {formatNumber(
                  Math.min(
                    (table.getState().pagination!.pageIndex + 1) * table.getState().pagination!.pageSize,
                    table.getFilteredRowModel().rows.length,
                  ),
                )}{' '}
                of {formatNumber(table.getFilteredRowModel().rows.length)} results
              </>
            )}
          </div>

          <div className="pagination-controls">
            <button onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()}>
              {'<<'}
            </button>
            <button onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
              {'<'}
            </button>
            <span className="page-info">
              Page {formatNumber((manualPagination ? pageIndex : table.getState().pagination!.pageIndex) + 1)}{' '}
              of {formatNumber(table.getPageCount())}
            </span>
            <button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
              {'>'}
            </button>
            <button
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
            >
              {'>>'}
            </button>
          </div>

          <select
            value={manualPagination ? pageSize : table.getState().pagination!.pageSize}
            onChange={e => {
              const newSize = Number(e.target.value);
              if (manualPagination && onPageSizeChange) {
                onPageSizeChange(newSize);
              } else {
                table.setPageSize(newSize);
              }
            }}
            className="page-size-select"
          >
            {pageSizeOptions.map(size => (
              <option key={size} value={size}>
                Show {size}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

export default TanStackGrid;
